// [PURPOSE]: Pure builders + thin dispatcher for feature-usage analytics events.
// [WHY]: Centralises event shape so all CW authoring + combat tracking uses a
//        consistent, system-agnostic taxonomy. Names are generic (content_authored,
//        combat_started) so non-D&D systems reuse them without taxonomy changes.
//        No PII: content_type is a category string; combat params are integer counts.
// [INTEGRATION]: Import buildContentAuthoredEvent / buildCombatStartedEvent / trackFeatureEvent
//   from this module at each CW save site and at the combat roll-initiative site.
//   trackEvent (services/analytics) is the silent no-op-safe underlying dispatcher.
// [CREATED]: A32 — 2026-06-01

import { trackEvent } from '../services/analytics';

/**
 * Build a content_authored event payload.
 *
 * @param {string} contentType — system-agnostic category (e.g. 'spell', 'class', 'race')
 * @param {{ isEdit?: boolean }} [options]
 * @returns {{ name: string, params: { content_type: string, is_edit: boolean } }}
 */
export const buildContentAuthoredEvent = (contentType, { isEdit = false } = {}) => ({
  name: 'content_authored',
  params: { content_type: String(contentType || 'unknown'), is_edit: !!isEdit },
});

/**
 * Build a combat_started event payload.
 * Counts combatants by kind so we understand PC vs NPC composition without PII.
 *
 * @param {Array<{ kind?: string }>} [combatants]
 * @returns {{ name: string, params: { combatant_count: number, pc_count: number, npc_count: number } }}
 */
export const buildCombatStartedEvent = (combatants = []) => {
  const list = Array.isArray(combatants) ? combatants : [];
  return {
    name: 'combat_started',
    params: {
      combatant_count: list.length,
      pc_count: list.filter((c) => c?.kind === 'pc').length,
      npc_count: list.filter((c) => c?.kind === 'npc').length,
    },
  };
};

/**
 * Build a shop_opened event payload (no params — pure feature-usage signal).
 * @returns {{ name: string, params: {} }}
 */
export const buildShopEvent = () => ({ name: 'shop_opened', params: {} });

/**
 * Build a bastion_viewed event payload (no params — pure feature-usage signal).
 * @returns {{ name: string, params: {} }}
 */
export const buildBastionViewedEvent = () => ({ name: 'bastion_viewed', params: {} });

/**
 * Build a party_viewed event payload.
 * Captures party size at view-time (no PII — integer member count only).
 * Avoids hooking the live-Firebase join/sync event (A33-class, not headless-testable)
 * while still recording the user's "party size" feature-usage ask.
 *
 * @param {{ size?: number }} [options]
 * @returns {{ name: string, params: { party_size: number } }}
 */
export const buildPartyEvent = ({ size = 0 } = {}) => {
  const n = Number(size);
  return {
    name: 'party_viewed',
    params: { party_size: Number.isFinite(n) && n > 0 ? n : 0 },
  };
};

/**
 * Pure mapper: active tab → the feature-usage event to fire (or null for untracked tabs).
 * System-agnostic — the tab keys are app-routing strings, not D&D content.
 * Lets the App.jsx tab-view effect stay a one-liner while the decision logic
 * is unit-testable in isolation (Rule 22 producer pattern).
 *
 * @param {string} tab — current activeTab key
 * @param {{ partySize?: number }} [context]
 * @returns {{ name: string, params: object } | null}
 */
export const buildTabViewEvent = (tab, { partySize = 0 } = {}) => {
  switch (tab) {
    case 'shop':
      return buildShopEvent();
    case 'bastion':
      return buildBastionViewedEvent();
    case 'party':
      return buildPartyEvent({ size: partySize });
    default:
      return null;
  }
};

/**
 * Fire a feature analytics event via trackEvent.
 * No-ops safely on a falsy or nameless event — never throws.
 *
 * @param {{ name: string, params?: object } | null | undefined} event
 */
export const trackFeatureEvent = (event) => {
  if (event && event.name) trackEvent(event.name, event.params || {});
};
